import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { NorthEastRounded } from "@mui/icons-material";
import { focusRingSx } from "../../overviewPanel";
import { cardSx, type TonalTokens } from "../../../polish/tokens";
import { cardHeadingSx } from "../feed/headings";
import { Change, Firm, Rating, Target, noteTextSx } from "../feed/NoteParts";
import { noteRow, noteSummary, type NoteRow } from "../feed/feedModel";
import type { ReportNote } from "../feed/types";

// Every note for the report, newest first, a row each: firm, rating (the
// move toned, ⇧ upgrade / ⇩ downgrade), the target from → to, its change,
// FMP's day and time, and the source. A row opens its article. Wide screens
// lay the columns out under a header; touch stacks each row onto two lines.

/**
 * Every column shares the width, the figures included, so on a wide card the
 * target, change and date spread out instead of bunching at the end; each
 * keeps a floor for its widest value.
 */
const COLUMNS =
  "minmax(0, 1.4fr) minmax(0, 1.2fr) minmax(148px, 1fr) minmax(80px, 0.7fr) minmax(120px, 0.9fr) 16px";
const COLUMN_GAP = { xs: 2, md: 3 } as const;
const HEADERS = ["Firm", "Rating", "Price target", "Change", "Date"];

interface WhenProps {
  row: NoteRow;
  tokens: TonalTokens;
}

/** "29 Oct 10:56": the day, then the time a step quieter. */
const When = ({ row, tokens }: WhenProps) => (
  <Typography
    data-testid="note-time"
    sx={{ ...noteTextSx(true), color: tokens.muted, textAlign: "right" }}
  >
    {row.date}
    <Box component="span" sx={{ color: tokens.faint, ml: 0.75 }}>
      {row.time}
    </Box>
  </Typography>
);

interface ReportNotesTableProps {
  notes: ReportNote[];
  /** The listing the page shows: a note about another one carries its ticker. */
  symbol: string;
  tokens: TonalTokens;
  touch: boolean;
}

export const ReportNotesTable = ({
  notes,
  symbol,
  tokens,
  touch,
}: ReportNotesTableProps) => {
  const rows = useMemo(
    () => notes.map((note) => noteRow(note, symbol)),
    [notes, symbol],
  );

  const rowSx = {
    ...focusRingSx,
    display: "grid",
    alignItems: "center",
    columnGap: COLUMN_GAP,
    mx: -1,
    px: 1,
    borderRadius: "8px",
    textDecoration: "none",
    color: "inherit",
    WebkitTapHighlightColor: "transparent",
    transition: "background-color 120ms ease",
    "@media (hover: hover)": {
      "&:hover": { backgroundColor: tokens.raised },
    },
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  } as const;
  const glyph = (
    <NorthEastRounded
      aria-hidden
      sx={{ fontSize: 14, color: tokens.faint, justifySelf: "end" }}
    />
  );

  return (
    <Box
      component="section"
      aria-labelledby="analyst-notes-title"
      data-testid="notes-card"
      sx={{ ...cardSx(tokens), px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}
    >
      <Typography
        id="analyst-notes-title"
        component="h2"
        sx={{ ...cardHeadingSx(tokens), mb: 1.5 }}
      >
        Analyst notes
      </Typography>

      {!touch && (
        <Box
          aria-hidden
          data-testid="notes-header"
          sx={{
            display: "grid",
            gridTemplateColumns: COLUMNS,
            columnGap: COLUMN_GAP,
            pb: 1,
          }}
        >
          {HEADERS.map((label, index) => (
            <Typography
              key={label}
              sx={{
                fontSize: { xs: "0.8125rem", md: "0.875rem" },
                color: tokens.faint,
                textAlign: index >= 2 ? "right" : "left",
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>
      )}

      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {rows.map((row) => (
          <Box
            component="li"
            key={row.id}
            data-testid="note-row"
            sx={{ borderTop: `1px solid ${tokens.hairline}` }}
          >
            <Box
              component="a"
              href={row.sourceUrl}
              target="_blank"
              rel="noopener"
              aria-label={`${noteSummary(row)}. Open the ${row.publisher} article`}
              sx={{
                ...rowSx,
                ...(touch && {
                  gridTemplateColumns: "minmax(0, 1fr) auto auto",
                  rowGap: 0.5,
                  py: 1.25,
                  minHeight: 44,
                }),
                ...(!touch && {
                  gridTemplateColumns: COLUMNS,
                  minHeight: 50,
                }),
              }}
            >
              {!touch && (
                <>
                  <Firm row={row} tokens={tokens} large />
                  <Rating row={row} tokens={tokens} large />
                  <Target row={row} tokens={tokens} large />
                  <Change row={row} tokens={tokens} large />
                  <When row={row} tokens={tokens} />
                </>
              )}
              {touch && (
                <>
                  <Box sx={{ gridColumn: "1 / 3", minWidth: 0 }}>
                    <Firm row={row} tokens={tokens} large />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    <When row={row} tokens={tokens} />
                    {glyph}
                  </Box>
                  <Rating row={row} tokens={tokens} large />
                  <Target row={row} tokens={tokens} large />
                  <Change row={row} tokens={tokens} large />
                </>
              )}
              {!touch && glyph}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
